import React from 'react';
import { isStaleChunkError, requestReload } from './chunkRecovery';

/**
 * React.lazy() wrapper that recovers from stale-deploy chunk-load failures.
 *
 * When a CloudFront/S3 deploy ships a new bundle, any tab that already
 * downloaded `index.html` for the previous build still holds references to
 * the old hashed chunk URLs (e.g. `SignupPage-vDpAc7hm.js`). When React
 * later calls the lazy factory, the dynamic import 404s and React's vanilla
 * `lazy()` stores `{ _result: undefined }`, so the next render throws
 * `TypeError: Cannot read properties of undefined (reading 'default')`
 * (Safari: `undefined is not an object (evaluating 'f._result.default')`).
 * That render error then bubbles to the Sentry ErrorBoundary, masking the
 * real cause (stale deployment) and creating P1 noise on every release.
 *
 * `lazyWithReload` short-circuits this race: on a recognised chunk-load
 * failure it calls `chunkRecovery.requestReload()` and returns a
 * never-resolving promise so React keeps showing the Suspense fallback
 * until the navigation tears down the page. Any other rejection (real
 * module-level bug) propagates so the ErrorBoundary still catches it.
 *
 * Drop-in replacement for `React.lazy` — same call shape, same return type.
 */
export function lazyWithReload<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    factory().catch((error) => {
      if (isStaleChunkError(error) && requestReload('lazy')) {
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }),
  );
}
